import React, { useRef } from "react";
import { MdOutlineCancel } from "react-icons/md";
import { IoCloudUploadOutline } from "react-icons/io5";
import { AiTwotoneFileUnknown } from "react-icons/ai";
import { BiSearch } from "react-icons/bi";
import { SmallLoader } from "./Loader";
import { DownloadButton } from "./Buttons";

const InputField = React.memo(({ label, name, error, className, textColor, type = "text", value, onChange, placeholder = "", required = false, disabled = false }) => {
    const handleChange= (e)=> {
      // console.log('e ===>', e.target);
        if(onChange){
            onChange(e);
        }
    }
  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={name}
          className={`flex text-left font-medium ${textColor || "text-gray-700"} mb-1`}
        >
          {label} 
          {required && (
            <span className="bg-red-100 text-red-600 rounded-full relative w-3.5 h-3.5">
              <span className=" absolute top-[-2px] left-[3px]">*</span>
            </span>
          )}
        </label>
      )}
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {error && (<div className=" text-red-700 text-sm font-medium text-left">{error}</div>)}
    </div>
  );
});

export default InputField;

export const SearchField = React.memo(({ name, loading, type = "text", value, onChange, placeholder = "", required = false, disabled = false }) => {
    const handleChange= (e)=> {
      // console.log('e ===>', e.target);
        if(onChange && !disabled){
            onChange(e);
        }
    }
  return (
    <div className={`flex items-center px-3 py-1 bg-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm border border-gray-300`}>
      {loading ? 
        <SmallLoader/> :
        <BiSearch className=" text-gray-400 text-xl"/>
      }
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        // disabled={disabled}
        className="w-full pl-1 py-1  focus:outline-none "
      />
    </div>
  );
});

export const InputFieldAnimated = React.memo(({ label, name, className, textColor, type = "text", value, onChange, placeholder = "", required = false, disabled = false, error = "" }) => {
    const handleChange= (e)=> {
        if(onChange){
            onChange(e);
        }
    }
  return (
    <div className={`input-group relative ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className={`label`}
        >
          {label}
        </label>
      )}
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="w-full"
      />
      {error && (<div className=" text-red-700 text-sm font-medium text-left">{error}</div>)}
    </div>
  );
});

// File upload component
export const FileInput = React.memo(
  ({ value = false, name = "file", placeholder = "Upload", label = "Upload", accept = "*",disabled, onSelect, onDownload = false, textColor = false, error = "" }) => {
    const inputRef = useRef(null); 

    // Click the hidden input when the button (label) is pressed
    const triggerInput = () => inputRef.current?.click();

    // Pass the chosen file back to the parent
    const handleFileChange = (event) => {
      const file = event.target.files?.[0];
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          name,
          value: file,
        },
      };
      onSelect?.(syntheticEvent);
    };
    // console.log("File path value: ",value)
    const handleCancel = ()=> {
      const syntheticEvent = {
        target: {
          name: inputRef.current.id,
          value: null,
        },
      };
      onSelect?.(syntheticEvent);
    }
    const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
    return (
      <div className="">
        {label && (
        <h2
          htmlFor={name}
          className={`block text-left font-medium ${textColor ? "text-blue-700" : "text-gray-700"} mb-1`}
        >
          {label}
        </h2>
      )}
        {/* Visible button */}
        {value ? 
        (<div className={`${disabled && "bg-gray-100"} w-full flex items-center justify-between gap-2 hi px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm`}>
          <span className="truncate overflow-hidden whitespace-nowrap"> 
            {/* {isObject(value) ? value?.name : <a href={value} target="_blank" rel="noreferrer">{ value?.split("\\")[value?.split("\\")?.length-1]}</a>} */}
            {isObject(value) ? value?.name : <a href={value} target="_blank" rel="noreferrer">{ value?.split("/")[value?.split("/")?.length-1]}</a>}
          </span>
          <button disabled={disabled} onClick={handleCancel} className={` ${disabled && "hidden"} flex items-center rounded-full shadow hover:bg-red-200 transition duration-300 ease-in-out w-fit`} >
            <MdOutlineCancel className=" text-red-600 h-6 w-6" />
          </button> 
        {onDownload && disabled && (
          <DownloadButton data={value} onClick={onDownload} tooltipName="Download Resume" />
        )}
        </div>) :
        ( 
          <div className="flex items-center">
            <button
              type="button"
              disabled={disabled}
              onClick={triggerInput}
              className={`${disabled && "bg-gray-100"} w-full flex flex-1 items-center justify-center gap-2 px-3 py-2.5  border border-gray-300 rounded-lg shadow-sm ${!disabled && "hover:bg-lime-100"} focus:outline-none navAnimation transition-colors`} >
              {disabled ?  <AiTwotoneFileUnknown className=" text-lime-600 h-6 w-6" /> :
              <IoCloudUploadOutline className=" text-lime-600 h-6 w-6" />}
              {disabled ? "No File" : placeholder}
            </button>
          </div>
        )}
        <input
          id={name}
          name={name}
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
        {error && (<div className=" text-red-700 text-sm font-medium text-left">{error}</div>)}
      </div>
    );
  }
);
